import {
	type CrawlAutomaticStopReason,
	type CrawlJobType,
	type CrawlMatrixDepth,
	type CrawlProductClassification,
	type CrawlRefreshInterval,
	type CrawlStopReason,
	ZYTE_SPENDING_LIMIT_USD,
} from "@repo/api-dtos"

export const JOB_TYPE_LABELS: Record<CrawlJobType, string> = {
	"cardmarket.catalogue-sync": "Catalogue download",
	"cardmarket.expansion-harvest": "Product page lookup",
	"cardmarket.product-offers": "Price crawl",
	"cardmarket.fair-prices": "Fair Price calculation",
	"cardmarket.sealed-offers": "Sealed product offers",
}

/** One line per task, shown under its on and off switch on the settings page. */
export const JOB_TYPE_DESCRIPTIONS: Record<CrawlJobType, string> = {
	"cardmarket.catalogue-sync":
		"Daily product list and price guide from Cardmarket. Free.",
	"cardmarket.expansion-harvest":
		"Finds the Cardmarket page of each new product.",
	"cardmarket.product-offers":
		"Opens each product page and records the cheapest prices.",
	"cardmarket.fair-prices":
		"Works out the Fair Price of each crawled product from its cheapest prices. Free.",
	"cardmarket.sealed-offers":
		"Crawls the Cardmarket offers of the sealed products our catalogue links to, for per-language Fair Prices. Off by default.",
}

/** Display name of a crawled website; unknown domains are shown as they are. */
export function domainLabel(domain: string): string {
	return domain === "cardmarket.com" ? "Cardmarket" : domain
}

/** Cardmarket language names by language id, as the sealed product language list shows them. */
export const CARDMARKET_LANGUAGE_NAMES: Readonly<Record<number, string>> = {
	1: "English",
	2: "French",
	3: "German",
	4: "Spanish",
	5: "Italian",
	6: "Simplified Chinese",
	7: "Japanese",
	8: "Portuguese",
	9: "Russian",
	10: "Korean",
	11: "Traditional Chinese",
	12: "Dutch",
	13: "Polish",
	14: "Czech",
	15: "Hungarian",
	16: "Indonesian",
	17: "Thai",
}

/** Name of a Cardmarket language id; an unknown id is shown as "Language N". */
export function cardmarketLanguageName(id: number): string {
	return CARDMARKET_LANGUAGE_NAMES[id] ?? `Language ${id}`
}

export const INTERVAL_LABELS: Record<CrawlRefreshInterval, string> = {
	daily: "Daily",
	every_3_days: "Every 3 days",
	weekly: "Weekly",
	every_2_weeks: "Every 2 weeks",
	monthly: "Monthly (30 days)",
	never: "Never",
}

/** How often a rule crawls, as the end of a sentence ("crawled every 3 days"). */
export const INTERVAL_PHRASES: Record<CrawlRefreshInterval, string> = {
	daily: "every day",
	every_3_days: "every 3 days",
	weekly: "every 7 days",
	every_2_weeks: "every 14 days",
	monthly: "every 30 days",
	never: "not crawled",
}

export const DEPTH_LABELS: Record<CrawlMatrixDepth, string> = {
	base: "First page only",
	full: "Everything: each language, finish, condition and seller type",
}

/** How much of a product page a rule reads, in a short phrase. */
export const DEPTH_PHRASES: Record<CrawlMatrixDepth, string> = {
	base: "first page only",
	full: "everything",
}

export const CLASSIFICATION_LABELS: Record<CrawlProductClassification, string> =
	{
		thin: "Few offers",
		liquid: "Many offers",
		unknown: "Never crawled",
	}

export const STOP_REASON_LABELS: Record<CrawlStopReason, string> = {
	operator: "Paused by an operator",
	throttled: "Paused because Cardmarket or Zyte was rate limiting us",
	account: "Paused because of a Zyte account problem",
	wasted_spend: "Paused because we were paying for pages we could not use",
	spend_cap: "Paused because the monthly spending cap was reached",
	daily_budget: "Paused because today's request budget was used up",
}

/** Title and line of an on and off switch on the settings page. */
export interface SwitchLabel {
	title: string
	description: string
}

const SPENDING_LIMIT = new Intl.NumberFormat("en-US").format(
	ZYTE_SPENDING_LIMIT_USD,
)

/** What each automatic pause does when on, and what happens when it is off. */
export const AUTOMATIC_STOP_SWITCHES: Record<
	CrawlAutomaticStopReason,
	SwitchLabel
> = {
	throttled: {
		title: "Pause when rate limited",
		description:
			"When Cardmarket or Zyte keeps rate limiting us, everything pauses until someone resumes it. Off: the crawl keeps going and only waits the few seconds Zyte asks for.",
	},
	account: {
		title: "Pause on Zyte account errors",
		description:
			"After 3 Zyte account or request errors in 5 minutes (wrong key, suspended account), everything pauses until someone resumes it. Off: the crawl keeps going and those requests fail.",
	},
	wasted_spend: {
		title: "Pause when paid pages are unusable",
		description:
			"When too many of the pages we pay for cannot be read, that task pauses until someone resumes it. Off: the task keeps going and keeps paying for them.",
	},
	spend_cap: {
		title: "Pause at the monthly spending cap",
		description: `When this month's spend reaches the cap under Advanced, everything pauses until next month. Off: no cap; Zyte still stops at its own ${SPENDING_LIMIT} USD limit.`,
	},
	daily_budget: {
		title: "Pause at the daily request limit",
		description:
			"When today's requests reach the limit under Advanced, everything pauses until midnight UTC. Off: no daily limit.",
	},
}

export const AUTOMATIC_SLOW_DOWN_SWITCH: SwitchLabel = {
	title: "Slow down when requests fail",
	description:
		"Lowers the speed when too many requests fail or are rate limited, and raises it again when they recover. Off: the crawl always runs at the requested speed.",
}

/** Who added a pause, in words: the automatic checks, an operator, or the crawler itself. */
export function stopSetByLabel(setBy: string): string {
	if (setBy === "tick") {
		return "the automatic checks"
	}
	if (setBy === "operator") {
		return "an operator"
	}
	return "the crawler"
}

export const TRANSPORT_MODE_LABELS = {
	http: "Plain request",
	browser: "Full browser",
} as const
